import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Loader2, TrendingUp, Users, DollarSign, Building2, Link2, Copy, RefreshCw,
  Phone, Clock, ArrowRight, Trophy, Share2, ExternalLink, ChevronRight, Sparkles, MessageCircle,
} from "lucide-react";

const TABS = [
  { key: "home", label: "My Home", icon: Sparkles },
  { key: "leads", label: "My Leads", icon: Users },
  { key: "commissions", label: "My Commissions", icon: DollarSign },
  { key: "body-shops", label: "My Body Shops", icon: Building2 },
  { key: "referral", label: "My Referral Link", icon: Link2 },
];

export default function SalesWorkspace() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "home");

  const switchTab = (k) => {
    setTab(k);
    setParams({ tab: k });
  };

  return (
    <div className="space-y-6" data-testid="sales-workspace">
      <div>
        <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-emerald-400 mb-2 flex items-center gap-1.5">
          <Trophy className="w-3 h-3" /> SALES WORKSPACE
        </div>
        <h1 className="font-display text-4xl font-semibold tracking-tight">Meu espaço de vendas</h1>
        <p className="text-sm text-slate-400 mt-2 max-w-2xl">
          Tudo que você precisa pra vender mais — leads, comissões, portais de body shop e o seu link pessoal de indicação.
        </p>
      </div>

      {/* Tab bar */}
      <div className="border-b border-slate-800 flex flex-wrap gap-1 -mx-1" data-testid="sales-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => switchTab(t.key)}
            data-testid={`sales-tab-${t.key}`}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition ${
              tab === t.key
                ? "text-emerald-300 border-emerald-500"
                : "text-slate-400 border-transparent hover:text-slate-200"
            }`}
          >
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "home" && <MyHome onGoto={switchTab} />}
      {tab === "leads" && <MyLeads onOpen={(id) => navigate(`/sales/prospects/${id}`)} />}
      {tab === "commissions" && <MyCommissions />}
      {tab === "body-shops" && <MyBodyShops />}
      {tab === "referral" && <MyReferral />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// My Home
// ═══════════════════════════════════════════════════════════
function MyHome({ onGoto }) {
  const [d, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/sales/me/dashboard").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-400 mx-auto" /></div>;
  if (!d) return null;

  return (
    <div className="space-y-6">
      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="my-home-kpis">
        <KpiCard label="Comissão pendente" value={`$${d.commissions.pending.toFixed(2)}`} sub="aguardando invoice ser paga" color="amber" />
        <KpiCard label="Comissão liberada" value={`$${d.commissions.released.toFixed(2)}`} sub="pronto pra receber" color="emerald" testid="kpi-released" />
        <KpiCard label="Pago no mês" value={`$${d.commissions.paid_this_month.toFixed(2)}`} sub="depositado" color="blue" />
        <KpiCard label="Leads ativos" value={d.active_leads} sub={`${d.won_last_30} won · ${d.lost_last_30} lost (30d)`} color="purple" />
      </div>

      {/* Second row */}
      <div className="grid md:grid-cols-3 gap-3">
        <KpiCard label="Taxa de conversão (30d)" value={`${d.conversion_rate}%`} sub="won ÷ (won + lost)" color="emerald" />
        <KpiCard label="Body shops seus" value={d.body_shops_count} sub="que você trouxe" color="blue" />
        <KpiCard label="Cliques no seu link" value={d.referral.hits} sub={`${d.referral.conversions} viraram cliente`} color="amber" />
      </div>

      {/* Follow-ups needed */}
      <Card className="bg-[#0B1120] border-rose-500/30 p-5" data-testid="follow-ups-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-rose-300 flex items-center gap-1.5">
              <Clock className="w-3 h-3" /> Precisam atenção HOJE
            </div>
            <h3 className="font-display text-lg font-semibold mt-1">Leads sem contato há +5 dias</h3>
          </div>
          <Button variant="outline" size="sm" onClick={() => onGoto("leads")} className="border-slate-700">
            Ver todos <ChevronRight className="w-3.5 h-3.5 ml-1" />
          </Button>
        </div>
        {d.follow_ups_needed.length === 0 ? (
          <p className="text-sm text-slate-400">🎉 Nenhum lead atrasado. Tá em dia com o follow-up!</p>
        ) : (
          <div className="space-y-2">
            {d.follow_ups_needed.map((p) => (
              <div key={p.id} className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-950 p-3">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm truncate">{p.company_name}</div>
                  <div className="text-[11px] text-slate-500">{p.contact_name || "—"} · Última atualização {new Date(p.updated_at).toLocaleDateString()}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {p.phone && (
                    <a href={`tel:${p.phone}`} className="inline-flex items-center gap-1 text-emerald-400 hover:text-emerald-300 text-[11px] border border-emerald-500/40 rounded px-2 py-1">
                      <Phone className="w-3 h-3" /> Ligar
                    </a>
                  )}
                  <Badge className="bg-amber-500/15 border-amber-500/40 text-amber-300 text-[10px] uppercase">{p.status}</Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function KpiCard({ label, value, sub, color, testid }) {
  const c = {
    amber: "border-amber-500/40 bg-amber-500/5",
    emerald: "border-emerald-500/40 bg-emerald-500/5",
    blue: "border-blue-500/40 bg-blue-500/5",
    purple: "border-purple-500/40 bg-purple-500/5",
  }[color] || "border-slate-800 bg-slate-900";
  const val = {
    amber: "text-amber-300",
    emerald: "text-emerald-300",
    blue: "text-blue-300",
    purple: "text-purple-300",
  }[color] || "text-slate-200";
  return (
    <Card className={`${c} p-4`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">{label}</div>
      <div className={`text-2xl font-mono font-bold ${val}`}>{value}</div>
      <div className="text-[10px] text-slate-500 mt-1">{sub}</div>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════
// My Leads
// ═══════════════════════════════════════════════════════════
function MyLeads({ onOpen }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/sales/me/prospects").then((r) => setRows(r.data || [])).finally(() => setLoading(false));
  }, []);
  const groups = ["new", "contacted", "meeting", "proposal", "won", "lost"];
  const grouped = groups.map((g) => ({ status: g, items: rows.filter((r) => r.status === g) }));
  if (loading) return <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-400 mx-auto" /></div>;
  return (
    <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="my-leads-kanban">
      {grouped.map((col) => (
        <div key={col.status} className="rounded-xl border border-slate-800 bg-[#0B1120] p-3">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2 flex justify-between items-center">
            <span>{col.status}</span>
            <span className="bg-slate-800 text-slate-300 rounded-full px-2 py-0.5">{col.items.length}</span>
          </div>
          <div className="space-y-2">
            {col.items.map((p) => (
              <button
                key={p.id}
                onClick={() => onOpen(p.id)}
                data-testid={`my-lead-${p.id}`}
                className="w-full text-left rounded-lg border border-slate-800 bg-slate-950 hover:border-emerald-500/50 p-2 transition"
              >
                <div className="text-xs font-medium truncate">{p.company_name}</div>
                <div className="text-[10px] text-slate-500 truncate">{p.contact_name || "—"}</div>
                {p.estimated_value && <div className="text-[10px] text-emerald-400 font-mono mt-1">${p.estimated_value.toFixed(2)}</div>}
              </button>
            ))}
            {col.items.length === 0 && <div className="text-[10px] text-slate-600 italic text-center py-4">—</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// My Commissions
// ═══════════════════════════════════════════════════════════
function MyCommissions() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/sales/me/commissions").then((r) => setRows(r.data || [])).finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-400 mx-auto" /></div>;
  const totals = rows.reduce((acc, c) => {
    const a = parseFloat(c.amount || 0);
    if (c.status === "pending") acc.pending += a;
    else if (c.status === "released") acc.released += a;
    else if (c.status === "paid") acc.paid += a;
    return acc;
  }, { pending: 0, released: 0, paid: 0 });
  return (
    <div className="space-y-4" data-testid="my-commissions">
      <div className="grid grid-cols-3 gap-3">
        <KpiCard label="Pending" value={`$${totals.pending.toFixed(2)}`} sub="invoice não paga" color="amber" />
        <KpiCard label="Released" value={`$${totals.released.toFixed(2)}`} sub="pronto pra pagar" color="emerald" />
        <KpiCard label="Paid" value={`$${totals.paid.toFixed(2)}`} sub="total pago" color="blue" />
      </div>
      <Card className="bg-[#0B1120] border-slate-800 p-4">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-wider text-slate-500">
            <tr className="border-b border-slate-800">
              <th className="text-left py-2">Invoice</th>
              <th className="text-left">Customer</th>
              <th className="text-right">Amount</th>
              <th className="text-right">Status</th>
              <th className="text-right">Created</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} className="border-b border-slate-800/50">
                <td className="py-2 font-mono text-xs">{c.invoice_number || c.invoice_id?.slice(0, 8)}</td>
                <td className="text-xs">{c.customer_name || "—"}</td>
                <td className="text-right font-mono text-emerald-300">${(c.amount || 0).toFixed(2)}</td>
                <td className="text-right">
                  <Badge className={
                    c.status === "paid" ? "bg-blue-500/15 border-blue-500/40 text-blue-300" :
                    c.status === "released" ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" :
                    "bg-amber-500/15 border-amber-500/40 text-amber-300"
                  }>{c.status}</Badge>
                </td>
                <td className="text-right text-[11px] text-slate-500">{c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-slate-500 text-sm">Nenhuma comissão ainda.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// My Body Shops
// ═══════════════════════════════════════════════════════════
function MyBodyShops() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/sales/me/body-shops").then((r) => setRows(r.data || [])).finally(() => setLoading(false));
  }, []);

  const copyLink = async (token) => {
    const url = `${window.location.origin}/submit/${token}`;
    try { await navigator.clipboard.writeText(url); toast.success("Link do body shop copiado"); } catch (e) { toast.error("Copy falhou"); }
  };
  const shareWhatsApp = (shop, token) => {
    const url = `${window.location.origin}/submit/${token}`;
    const msg = `Olá ${shop.name}! Aqui está seu portal exclusivo TrueLine pra enviar estimates e ver cotação instantânea: ${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  if (loading) return <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-400 mx-auto" /></div>;
  return (
    <div className="space-y-3" data-testid="my-body-shops">
      {rows.length === 0 ? (
        <Card className="p-12 text-center bg-[#0B1120] border-slate-800">
          <Building2 className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <div className="font-display text-lg mb-1">Nenhum body shop ainda</div>
          <p className="text-sm text-slate-500 mb-3">Quando um dos seus prospects virar "won", ele aparece aqui.</p>
        </Card>
      ) : rows.map((s) => (
        <Card key={s.id} className="bg-[#0B1120] border-slate-800 p-4" data-testid={`my-shop-${s.id}`}>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="font-display text-lg font-medium truncate">{s.name}</div>
              <div className="text-xs text-slate-500 mt-1">
                {s.default_discount_pct ? `Desconto: ${s.default_discount_pct}%` : "Sem desconto acordado"} · {s.city || "—"}, {s.state || "—"}
              </div>
              <div className="text-[11px] text-emerald-400 mt-2">
                Receita este mês: <span className="font-mono">${(s.revenue_this_month || 0).toFixed(2)}</span>
              </div>
            </div>
            <div className="flex flex-col gap-1.5 shrink-0">
              {s.submission_token && (
                <>
                  <Button size="sm" onClick={() => shareWhatsApp(s, s.submission_token)} className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs" data-testid={`share-wa-${s.id}`}>
                    <MessageCircle className="w-3.5 h-3.5 mr-1" /> WhatsApp
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => copyLink(s.submission_token)} className="border-slate-700 text-xs">
                    <Copy className="w-3.5 h-3.5 mr-1" /> Copy Link
                  </Button>
                </>
              )}
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// My Referral
// ═══════════════════════════════════════════════════════════
function MyReferral() {
  const [d, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = () => api.get("/sales/me/referral").then((r) => setData(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);
  const copy = async () => {
    try { await navigator.clipboard.writeText(d.url); toast.success("Link copiado"); } catch (e) { toast.error("Copy falhou"); }
  };
  const rotate = async () => {
    if (!window.confirm("Rotacionar o link? O antigo deixa de funcionar.")) return;
    await api.post("/sales/me/referral/rotate");
    toast.success("Novo link gerado");
    load();
  };
  if (loading) return <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-400 mx-auto" /></div>;
  if (!d) return null;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(d.url)}&bgcolor=030712&color=10B981`;
  return (
    <div className="grid md:grid-cols-2 gap-6" data-testid="my-referral">
      <Card className="bg-[#0B1120] border-emerald-500/30 p-6 space-y-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-400 mb-1">SEU LINK PESSOAL</div>
          <h3 className="font-display text-xl font-semibold">Todo body shop que se cadastrar por esse link é seu — pra sempre.</h3>
          <p className="text-xs text-slate-400 mt-2 leading-relaxed">
            Comissões dos jobs futuros desses body shops caem no seu bolso automaticamente. Compartilhe no cartão, WhatsApp, assinatura de email.
          </p>
        </div>
        <div className="rounded-lg bg-slate-950 border border-slate-800 p-3 font-mono text-sm text-emerald-300 break-all select-all" data-testid="my-referral-url">
          {d.url}
        </div>
        <div className="flex gap-2">
          <Button onClick={copy} className="flex-1 bg-emerald-600 hover:bg-emerald-500" data-testid="copy-referral-btn">
            <Copy className="w-4 h-4 mr-2" /> Copiar link
          </Button>
          <Button variant="outline" onClick={rotate} className="border-amber-500/40 text-amber-300" data-testid="rotate-referral-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Novo link
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-3 pt-4 border-t border-slate-800">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Cliques</div>
            <div className="text-2xl font-mono font-bold text-blue-300" data-testid="referral-hits">{d.hits}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Conversões</div>
            <div className="text-2xl font-mono font-bold text-emerald-300" data-testid="referral-conversions">{d.conversions}</div>
          </div>
        </div>
      </Card>

      <Card className="bg-[#0B1120] border-slate-800 p-6 text-center flex flex-col items-center justify-center">
        <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-3">QR CODE PRA CARTÃO / WHATSAPP</div>
        <div className="bg-slate-950 border-2 border-emerald-500/30 rounded-lg p-4 inline-block">
          <img src={qrUrl} alt="Referral QR" className="rounded" data-testid="referral-qr" />
        </div>
        <p className="text-xs text-slate-500 mt-4 max-w-xs">
          Salva essa imagem, coloca no cartão de visita ou fixa no vidro do carro. O body shop escaneia com a câmera do celular e cai direto no seu link.
        </p>
      </Card>
    </div>
  );
}
